'use client';

import { useIsVisible } from '@hc-frontend/core-ui';
import { toPhoneE164 } from '@hc-frontend/core-utils-format';
import {
  MedicareSupplementPlanSummaryContent,
  PlanSummaryCard,
} from '@hc-frontend/features-quotes';
import { usePathname, useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { loadQuotes } from '../../actions/load-quotes';
import { TypLinkEnroll } from '../universal-call-component/enroll-link';
import { UccModalButtonTrigger } from '../universal-call-component/modal-button-trigger';
import { UccModalProvider } from '../universal-call-component/modal-provider';
import type { MedicareSupplementQuotesResultsProps } from './medicare-supplement-quotes-results.types';

export function MedicareSupplementQuotesResults({
  initialPlans,
  initialPage,
  total,
  pageSize,
  quotesProductType,
  userId,
  resultsBanner,
  UccModal,
  stickyBanner,
  phoneUser,
  phoneCampaign,
  isOEP,
}: MedicareSupplementQuotesResultsProps) {
  const spinner = useRef<HTMLDivElement | null>(null);
  const page = useRef<number>(initialPage);
  const isLoading = useRef<boolean>(false);
  const isVisible = useIsVisible(spinner);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState(initialPlans);
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // Loads more quotes when the spinner is visible
  useEffect(() => {
    async function load() {
      setError('');
      isLoading.current = true;
      page.current += 1;

      const result = await loadQuotes({
        userId,
        quotesProductType,
        pageSize,
        page: page.current,
        searchParams: Object.fromEntries(searchParams.entries()),
        isOEP,
      });

      isLoading.current = false;

      if (result.success) {
        setPlans((currentPlans) => [...currentPlans, ...result.data.plans]);
        setError('');
      } else {
        setError('An error occurred loading more plans.');
      }
    }

    if (isVisible && !isLoading.current) {
      load();
    }
  }, [isVisible, quotesProductType, pageSize, searchParams, userId, isOEP]);

  let actionButton: ReactNode | null = null;

  if (typeof phoneUser === 'string' && phoneUser.length > 0) {
    actionButton = (
      <UccModalButtonTrigger>
        <span className="hc-btn--primary-card-small block px-60-60">Apply</span>
      </UccModalButtonTrigger>
    );
  } else if (phoneCampaign) {
    actionButton = (
      <TypLinkEnroll
        href={`tel:${toPhoneE164(phoneCampaign.phoneNumber)}`}
        className="hc-btn--primary-card-small block px-60-60"
      >
        Apply
      </TypLinkEnroll>
    );
  }

  return (
    <UccModalProvider>
      {UccModal}
      <div className="flex flex-col gap-24-24">
        {stickyBanner}
        {plans.map((plan, index) => (
          <div className="flex flex-col gap-24-24" key={plan.id}>
            <PlanSummaryCard
              planSummary={plan}
              listPosition={index + 1}
              listTotal={total}
              listPage={page.current + 1}
              productType="MEDICARE"
              link={{
                cta: 'View Plan Detail',
                href: `${pathname}${plan.id}`,
              }}
            >
              <MedicareSupplementPlanSummaryContent
                planSummary={plan}
                actionButton={actionButton}
                benefitHighlights={[
                  'HomeHealthCare',
                  'PartBExcess',
                  'TravelEmergency',
                  'Nursing',
                ]}
              />
            </PlanSummaryCard>
            {(index + 1) % 3 === 0 ? resultsBanner : null}
          </div>
        ))}
        {plans.length < 3 ? resultsBanner : null}
        {error ? (
          <p className="w-full text-center text-error-800" ref={spinner}>
            {error}
          </p>
        ) : null}
        {plans.length < total && !error ? (
          /* TODO: define spinner icon */
          <p className="w-full text-center" ref={spinner}>
            Loading more plans...
          </p>
        ) : null}
      </div>
    </UccModalProvider>
  );
}
